import React, { useEffect, useMemo, useState } from "react";
import {
  ChartScatter,
  ChevronDown,
  Plus,
  Target,
  Trash2,
} from "lucide-react";

import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Checkbox } from "components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Textarea } from "components/ui/textarea";
import MultiSelect from "components/forms/MultiSelect";
import { cn } from "lib/utils";

function deepClone(input) {
  try {
    return JSON.parse(JSON.stringify(input));
  } catch (e) {
    return input;
  }
}

function getDefaultAnalytics(dimKeys = ["dimension_1", "dimension_2"]) {
  return {
    schema_version: "1.0",
    dimensions: [
      {
        key: dimKeys[0],
        name: { en: "Dimension 1" },
        scale: { min: 1, max: 7, target: "high" },
      },
      {
        key: dimKeys[1],
        name: { en: "Dimension 2" },
        scale: { min: 1, max: 7, target: "high" },
      },
    ],
    analyses: {
      id: `${dimKeys[0]}_x_${dimKeys[1]}_v1`,
      type: "quadrant",
      name: { en: `${dimKeys[0]} × ${dimKeys[1]} Quadrants` },
      dimensions: [
        { key: dimKeys[0], axis: "x", invert: false },
        { key: dimKeys[1], axis: "y", invert: false },
      ],
      zones: [],
      resolution: { strategy: "highest_priority", fallback_zone: "unclassified" },
      defaults: { unclassified: { label: { en: "Unclassified" } } },
    },
  };
}

function flattenQuestionsTree(questionsRoot) {
  const flat = [];
  const dims = questionsRoot?.dimension || questionsRoot?.dimensions || [];
  (dims || []).forEach((dim, dimIdx) => {
    const factors = dim?.factors || [];
    (factors || []).forEach((factor, factorIdx) => {
      const qs = factor?.questions || [];
      (qs || []).forEach((q, qIdx) => {
        flat.push({ dimIdx, factorIdx, qIdx, q });
      });
    });
  });
  return flat;
}

const TARGET_OPTIONS = [
  { key: "high", text: "High is good", value: "high" },
  { key: "low", text: "Low is good", value: "low" },
];

const RESOLUTION_STRATEGIES = [
  { key: "highest_priority", text: "Highest priority", value: "highest_priority" },
  { key: "first_match", text: "First matching zone", value: "first_match" },
];

const AXIS_OPTIONS = [
  { key: "x", icon: ChartScatter, label: "X axis" },
  { key: "y", icon: Target, label: "Y axis" },
];

const OP_OPTIONS = [
  { key: "lt", text: "<", value: "<" },
  { key: "lte", text: "<=", value: "<=" },
  { key: "gt", text: ">", value: ">" },
  { key: "gte", text: ">=", value: ">=" },
  { key: "eq", text: "=", value: "=" },
];

const ZoneRuleRow = ({
  rule,
  dimKeys,
  onChange,
  onRemove,
}) => {
  return (
    <div className="grid gap-2 rounded-lg border border-border/50 bg-muted/20 p-3 md:grid-cols-[minmax(0,220px)_minmax(0,120px)_minmax(0,1fr)_auto]">
      <Select
        value={rule?.dimension || dimKeys[0]}
        onValueChange={(value) => onChange({ ...rule, dimension: value })}
      >
        <SelectTrigger className="justify-between">
          <SelectValue placeholder="Dimension" />
        </SelectTrigger>
        <SelectContent>
          {dimKeys.map((k) => (
            <SelectItem key={`rule-dim-${k}`} value={k}>
              {k || "(unset)"}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={rule?.op || ">="}
        onValueChange={(value) => onChange({ ...rule, op: value })}
      >
        <SelectTrigger className="justify-between">
          <SelectValue placeholder="Operator" />
        </SelectTrigger>
        <SelectContent>
          {OP_OPTIONS.map((option) => (
            <SelectItem key={`rule-op-${option.value}`} value={option.value}>
              {option.text}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Input
        type="number"
        placeholder="Value"
        value={rule?.value ?? 0}
        onChange={(e) => onChange({ ...rule, value: Number(e.target.value) || 0 })}
      />

      <Button
        type="button"
        size="icon"
        variant="ghost"
        onClick={onRemove}
        className="text-destructive"
        aria-label="Remove rule"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
};

const AnalyticsManagerModal = ({ open, onClose, questions, onSave }) => {
  const [workingQuestions, setWorkingQuestions] = useState({});
  const [analytics, setAnalytics] = useState(getDefaultAnalytics());

  useEffect(() => {
    if (open) {
      const base = deepClone(questions || {});
      const a = deepClone(base?.analytics) || getDefaultAnalytics();
      if (!Array.isArray(a.dimensions)) a.dimensions = getDefaultAnalytics().dimensions;
      if (a.dimensions.length < 2) {
        const pad = getDefaultAnalytics().dimensions.slice(a.dimensions.length);
        a.dimensions = [...a.dimensions, ...pad];
      }
      if (a.dimensions.length > 2) a.dimensions = a.dimensions.slice(0, 2);
      if (Array.isArray(a.analyses)) {
        a.analyses = a.analyses[0] || getDefaultAnalytics(a.dimensions.map((d) => d.key)).analyses;
      }
      if (!a.analyses || typeof a.analyses !== "object") {
        a.analyses = getDefaultAnalytics(a.dimensions.map((d) => d.key)).analyses;
      }
      setWorkingQuestions(base);
      setAnalytics(a);
    }
  }, [open, questions]);

  const flatQs = useMemo(() => flattenQuestionsTree(workingQuestions), [workingQuestions]);

  const questionOptions = useMemo(() => {
    const byId = new Map();
    flatQs.forEach(({ q }, i) => {
      const id = q?.id;
      if (id === undefined || id === null) return;
      const text = q?.q || `Question ${i + 1}`;
      if (!byId.has(id)) byId.set(id, { key: id, text: `${text} (id ${id})`, value: id });
    });
    return Array.from(byId.values());
  }, [flatQs]);

  const dimKeys = (analytics?.dimensions || []).map((d) => d.key || "");

  const setAxis = (which, field, value) => {
    setAnalytics((prev) => {
      const next = deepClone(prev);
      const analysis = next.analyses || {};
      const dims = analysis.dimensions || [];
      const idx = which === "x" ? 0 : 1;
      const curr = { ...(dims[idx] || {}) };
      curr[field] = field === "invert" ? !!value : value;
      dims[idx] = curr;
      analysis.dimensions = dims;
      next.analyses = analysis;
      return next;
    });
  };

  const setDimensionField = (index, field, value) => {
    setAnalytics((prev) => {
      const next = deepClone(prev);
      const dims = [...(next.dimensions || [])];
      const curr = { ...(dims[index] || {}) };
      if (field === "key") {
        curr.key = value;
        curr.name = { ...(curr.name || {}), en: value || "" };
      } else {
        curr[field] = value;
      }
      dims[index] = curr;
      next.dimensions = dims;
      return next;
    });
  };

  const setDimensionScale = (index, part, value) => {
    setAnalytics((prev) => {
      const next = deepClone(prev);
      const dims = [...(next.dimensions || [])];
      const scale = { ...(dims[index]?.scale || { min: 1, max: 7, target: "high" }) };
      scale[part] = part === "target" ? value : Number(value || 0);
      dims[index] = { ...(dims[index] || {}), scale };
      next.dimensions = dims;
      return next;
    });
  };

  const setSelectedIdsForDimension = (index, ids) => {
    setAnalytics((prev) => {
      const next = deepClone(prev);
      const dims = [...(next.dimensions || [])];
      const dim = { ...(dims[index] || {}) };
      dim.question_ids = (ids || []).map((n) => Number(n)).filter((n) => !Number.isNaN(n));
      dims[index] = dim;
      next.dimensions = dims;
      return next;
    });
  };

  const addZone = () => {
    setAnalytics((prev) => {
      const next = deepClone(prev);
      const analysis = next.analyses || {};
      const zones = [...(analysis.zones || [])];
      zones.push({ id: `zone_${zones.length + 1}`, label: { en: "Zone" }, priority: 50, rules: [] });
      analysis.zones = zones;
      next.analyses = analysis;
      return next;
    });
  };

  const updateZone = (idx, field, value) => {
    setAnalytics((prev) => {
      const next = deepClone(prev);
      const analysis = next.analyses || {};
      const zones = [...(analysis.zones || [])];
      zones[idx] = {
        ...(zones[idx] || {}),
        [field]:
          field === "priority"
            ? Number(value || 0)
            : field === "label"
            ? { ...(zones[idx]?.label || {}), en: value }
            : value,
      };
      analysis.zones = zones;
      next.analyses = analysis;
      return next;
    });
  };

  const removeZone = (idx) => {
    setAnalytics((prev) => {
      const next = deepClone(prev);
      const analysis = next.analyses || {};
      analysis.zones = (analysis.zones || []).filter((_, i) => i !== idx);
      next.analyses = analysis;
      return next;
    });
  };

  const addRule = (zoneIdx) => {
    setAnalytics((prev) => {
      const next = deepClone(prev);
      const analysis = next.analyses || {};
      const zones = [...(analysis.zones || [])];
      const firstDim = (next.dimensions || [])[0]?.key || "";
      const rules = [...(zones[zoneIdx]?.rules || [])];
      rules.push({ dimension: firstDim, op: ">=", value: 0 });
      zones[zoneIdx] = { ...(zones[zoneIdx] || {}), rules };
      analysis.zones = zones;
      next.analyses = analysis;
      return next;
    });
  };

  const updateRule = (zoneIdx, ruleIdx, nextRule) => {
    setAnalytics((prev) => {
      const next = deepClone(prev);
      const analysis = next.analyses || {};
      const zones = [...(analysis.zones || [])];
      const zone = { ...(zones[zoneIdx] || {}) };
      const rules = [...(zone.rules || [])];
      rules[ruleIdx] = nextRule;
      zone.rules = rules;
      zones[zoneIdx] = zone;
      analysis.zones = zones;
      next.analyses = analysis;
      return next;
    });
  };

  const removeRule = (zoneIdx, ruleIdx) => {
    setAnalytics((prev) => {
      const next = deepClone(prev);
      const analysis = next.analyses || {};
      const zones = [...(analysis.zones || [])];
      const zone = { ...(zones[zoneIdx] || {}) };
      zone.rules = (zone.rules || []).filter((_, i) => i !== ruleIdx);
      zones[zoneIdx] = zone;
      analysis.zones = zones;
      next.analyses = analysis;
      return next;
    });
  };

  const handleSave = () => {
    const out = deepClone(questions || {});
    out.analytics = deepClone(analytics);
    onSave(out);
  };

  const handleDialogChange = (nextOpen) => {
    if (!nextOpen) onClose && onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent className="flex h-[90dvh] w-full max-w-6xl flex-col overflow-hidden p-0">
        <DialogHeader className="shrink-0 px-6 pt-6">
          <DialogTitle>Manage Analytics</DialogTitle>
          <DialogDescription>
            Configure analytic dimensions, tag questions, and define quadrant rules.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-1 min-h-0 flex-col gap-8 overflow-y-auto px-6 pb-6">
          <div className="grid gap-6 md:grid-cols-2">
            {(analytics?.dimensions || []).slice(0, 2).map((dim, i) => (
              <Card key={`dim-${i}`} className="border border-border/60">
                <CardHeader className="pb-4">
                  <CardTitle className="text-base font-semibold">Dimension {i + 1}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <Input
                      placeholder="Key (e.g., engagement)"
                      value={dim?.key || ""}
                      onChange={(e) => setDimensionField(i, "key", e.target.value)}
                    />
                    <div className="grid gap-3 md:grid-cols-3">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={dim?.scale?.min ?? 1}
                        onChange={(e) => setDimensionScale(i, "min", e.target.value)}
                      />
                      <Input
                        type="number"
                        placeholder="Max"
                        value={dim?.scale?.max ?? 7}
                        onChange={(e) => setDimensionScale(i, "max", e.target.value)}
                      />
                      <Select
                        value={dim?.scale?.target || "high"}
                        onValueChange={(value) => setDimensionScale(i, "target", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Target" />
                        </SelectTrigger>
                        <SelectContent>
                          {TARGET_OPTIONS.map((option) => (
                            <SelectItem key={`target-${option.value}`} value={option.value}>
                              {option.text}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-medium text-foreground">Questions for this dimension</p>
                    <MultiSelect
                      options={questionOptions}
                      value={(dim?.question_ids || []).map((id) => String(id))}
                      onChange={(value) => setSelectedIdsForDimension(i, value)}
                      placeholder="Select questions"
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="border border-border/60">
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-semibold">Quadrant configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <p className="text-sm font-semibold text-foreground">Axes</p>
                <div className="grid gap-4 md:grid-cols-2">
                  {AXIS_OPTIONS.map(({ key, icon: Icon, label }) => (
                    <div
                      key={key}
                      className="flex flex-col gap-3 rounded-lg border border-border/50 bg-muted/10 p-4"
                    >
                      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                        <Icon className="h-4 w-4" /> {label}
                      </div>
                      <Select
                        value={analytics?.analyses?.dimensions?.[key === "x" ? 0 : 1]?.key || dimKeys[key === "x" ? 0 : 1]}
                        onValueChange={(value) => setAxis(key, "key", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select dimension" />
                        </SelectTrigger>
                        <SelectContent>
                          {dimKeys.map((dk) => (
                            <SelectItem key={`axis-${key}-${dk}`} value={dk}>
                              {dk || "(unset)"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <label className="flex items-center gap-2 text-sm font-medium text-foreground">
                        <Checkbox
                          checked={!!analytics?.analyses?.dimensions?.[key === "x" ? 0 : 1]?.invert}
                          onCheckedChange={(checked) => setAxis(key, "invert", !!checked)}
                        />
                        Invert axis
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-sm font-semibold text-foreground">Zones</p>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={addZone}
                    className="inline-flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" /> Add zone
                  </Button>
                </div>

                {(analytics?.analyses?.zones || []).length === 0 && (
                  <div className="rounded-lg border border-dashed border-border/70 p-6 text-center text-sm text-muted-foreground">
                    No zones yet. Add zones to define quadrant rules.
                  </div>
                )}

                <div className="space-y-6">
                  {(analytics?.analyses?.zones || []).map((zone, zi) => (
                    <div key={`zone-${zi}`} className="space-y-4 rounded-lg border border-border/60 bg-card/40 p-5">
                      <div className="flex flex-wrap items-center gap-3">
                        <Input
                          placeholder="Zone id"
                          value={zone?.id || ""}
                          onChange={(e) => updateZone(zi, "id", e.target.value)}
                          className="w-full max-w-xs"
                        />
                        <Input
                          placeholder="Label (English)"
                          value={zone?.label?.en || ""}
                          onChange={(e) => updateZone(zi, "label", e.target.value)}
                          className="w-full max-w-sm"
                        />
                        <Input
                          type="number"
                          placeholder="Priority"
                          value={zone?.priority ?? 50}
                          onChange={(e) => updateZone(zi, "priority", e.target.value)}
                          className="w-full max-w-[140px]"
                        />
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={() => removeZone(zi)}
                          className="ml-auto text-destructive"
                          aria-label="Remove zone"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="text-sm font-medium text-foreground">Rules</span>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => addRule(zi)}
                            className="inline-flex items-center gap-2"
                          >
                            <Plus className="h-4 w-4" /> Add rule
                          </Button>
                        </div>

                        {(zone?.rules || []).length === 0 && (
                          <p className="rounded-md border border-dashed border-border/60 p-4 text-sm text-muted-foreground">
                            No rules yet.
                          </p>
                        )}

                        <div className="space-y-3">
                          {(zone?.rules || []).map((rule, ri) => (
                            <ZoneRuleRow
                              key={`rule-${zi}-${ri}`}
                              rule={rule}
                              dimKeys={dimKeys}
                              onChange={(nextRule) => updateRule(zi, ri, nextRule)}
                              onRemove={() => removeRule(zi, ri)}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-sm font-semibold text-foreground">Resolution</p>
                <div className="grid gap-4 md:grid-cols-2">
                  <Select
                    value={analytics?.analyses?.resolution?.strategy || "highest_priority"}
                    onValueChange={(value) =>
                      setAnalytics((prev) => {
                        const next = deepClone(prev);
                        const analysis = next.analyses || {};
                        analysis.resolution = {
                          ...(analysis.resolution || {}),
                          strategy: value,
                        };
                        next.analyses = analysis;
                        return next;
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Resolution strategy" />
                    </SelectTrigger>
                    <SelectContent>
                      {RESOLUTION_STRATEGIES.map((option) => (
                        <SelectItem key={`res-${option.value}`} value={option.value}>
                          {option.text}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Input
                    placeholder="Fallback zone id"
                    value={analytics?.analyses?.resolution?.fallback_zone || ""}
                    onChange={(e) =>
                      setAnalytics((prev) => {
                        const next = deepClone(prev);
                        const analysis = next.analyses || {};
                        analysis.resolution = {
                          ...(analysis.resolution || {}),
                          fallback_zone: e.target.value,
                        };
                        next.analyses = analysis;
                        return next;
                      })
                    }
                  />
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-sm font-semibold text-foreground">Notes</p>
                <Textarea
                  rows={3}
                  placeholder="Optional description"
                  value={analytics?.analyses?.description || ""}
                  onChange={(e) =>
                    setAnalytics((prev) => {
                      const next = deepClone(prev);
                      next.analyses = {
                        ...(next.analyses || {}),
                        description: e.target.value,
                      };
                      return next;
                    })
                  }
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="shrink-0 border-t border-border/60 bg-muted/10 px-6 py-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={handleSave} className="inline-flex items-center gap-2">
            <ChevronDown className="h-4 w-4" /> Save analytics
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AnalyticsManagerModal;


